import { useState } from 'react';
import { personalInfo } from '../data/content';

export const useFormSubmit = () => {
  const [status, setStatus] = useState('idle'); // 'idle', 'loading', 'success', 'error'
  const [error, setError] = useState(null);

  const submitForm = async (payload) => {
    setStatus('loading');
    setError(null);

    const baseUrl = import.meta.env.VITE_SUPABASE_URL || 'https://<YOUR_PROJECT_REF>.supabase.co';
    const functionName = import.meta.env.VITE_SUPABASE_FUNNEL_FUNCTION || '<YOUR_FUNNEL_FUNCTION_NAME>';
    const anonKey = import.meta.env.VITE_SUPABASE_ANON_KEY || '<YOUR_SUPABASE_ANON_KEY>';
    const workspaceId = import.meta.env.VITE_WORKSPACE_ID || '<YOUR_WORKSPACE_ID>';
    const endpoint = `${baseUrl}/functions/v1/${functionName}?workspaceId=${workspaceId}`;

    const finalPayload = {
      ...payload,
      workspaceId,
    };

    try {
      const response = await fetch(endpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${anonKey}`,
        },
        body: JSON.stringify(finalPayload),
      });

      if (!response.ok) {
        throw new Error(`Error: ${response.status} ${response.statusText}`);
      }

      // Format payload for email body
      let bodyText = "New Form Submission:\n\n";
      for (const [key, value] of Object.entries(payload)) {
        // Skip excessively long base64 strings
        let displayValue = value;
        if (typeof value === 'string' && value.length > 500) {
          displayValue = "[Truncated string / File Attachment]";
        }
        bodyText += `${key}:\n${displayValue}\n\n`;
      }

      // Helper function to map listName to Netlify form names
      const getNetlifyFormName = (listName) => {
        switch (listName) {
          case 'Contact Submission':
            return 'contact';
          case 'Newsletter':
            return 'newsletter';
          case 'Case Study Submission':
            return 'case-study';
          case 'ROI Calculator':
            return 'roi-audit';
          case 'Resource Download':
            return 'resource-download';
          default:
            return 'contact'; // Fallback
        }
      };

      // Background email delivery via Netlify Forms
      if (payload.listName !== 'Resource Download') {
        try {
          const formName = getNetlifyFormName(payload.listName);

          // Format payload into FormData for Netlify Forms
          const formData = new FormData();
          formData.append('form-name', formName);

          // Helper to convert base64 to Blob
          const base64ToBlob = (base64, mimeType) => {
            const byteCharacters = atob(base64);
            const byteArrays = [];
            for (let offset = 0; offset < byteCharacters.length; offset += 512) {
              const slice = byteCharacters.slice(offset, offset + 512);
              const byteNumbers = new Array(slice.length);
              for (let i = 0; i < slice.length; i++) {
                byteNumbers[i] = slice.charCodeAt(i);
              }
              const byteArray = new Uint8Array(byteNumbers);
              byteArrays.push(byteArray);
            }
            return new Blob(byteArrays, { type: mimeType });
          };

          for (const [key, value] of Object.entries(payload)) {
            // Handle preferences object (e.g. from Newsletter form)
            if (key === 'preferences' && typeof value === 'object' && value !== null) {
              for (const [prefKey, prefValue] of Object.entries(value)) {
                if (prefValue) {
                  formData.append(prefKey, 'on');
                }
              }
            } else if (key === 'brief' && typeof value === 'string' && value.length > 500) {
              const mimeType = payload.briefMimeType || 'application/octet-stream';
              const fileName = payload.briefFileName || 'file';
              try {
                const blob = base64ToBlob(value, mimeType);
                formData.append(key, blob, fileName);
              } catch (e) {
                console.error("Failed to convert brief base64 to blob", e);
              }
            } else if (key === 'assets' && typeof value === 'string' && value.length > 500) {
              const mimeType = payload.assetMimeType || 'application/octet-stream';
              const fileName = payload.assetsFileName || 'file';
              try {
                const blob = base64ToBlob(value, mimeType);
                formData.append(key, blob, fileName);
              } catch (e) {
                console.error("Failed to convert assets base64 to blob", e);
              }
            } else if (typeof value !== 'object') {
              formData.append(key, value);
            }
          }

          await fetch('/', {
            method: 'POST',
            body: formData
          });
        } catch (formErr) {
          console.error('Background Netlify Form failed to submit:', formErr);
          // We still return true if the primary supabase submit worked, just log the form failure.
        }
      }

      setStatus('success');
      return true;
    } catch (err) {
      console.error('Form submission error:', err);
      setError(err.message || 'An unexpected error occurred');
      setStatus('error');
      return false;
    }
  };

  const resetFormStatus = (newStatus = 'idle') => {
    setStatus(newStatus);
    setError(null);
  };

  return { status, error, submitForm, resetFormStatus, setStatus };
};
